import type { BaseComponentProps, WithChildren } from "@neru/types";
import React, { useRef, useState } from "react";

import type { Sizes } from "../../types";

export interface FilePickerProps extends BaseComponentProps, WithChildren {
  accept?: string;
  errorText?: string;
  helperText?: string;
  label?: string;
  maxFileSize?: number | string;
  maxFiles?: number | string;
  name: string;
  onChange?: (files: FileList | null) => void;
  singleFile?: boolean;
  size?: Exclude<Sizes, "condensed" | "super-condensed">;
}

const sizeStyles = {
  expanded: "p-4 h-28",
  normal: "p-4",
};

export const FilePicker = ({
  accept,
  children,
  "data-testid": testId = "file-picker",
  errorText, // 256MB default
  helperText,
  label,
  maxFiles,
  maxFileSize = 256 * 1024 * 1024,
  name,
  onChange,
  singleFile = false,
  size = "normal",
}: FilePickerProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;

    if (files) {
      const validFiles = Array.from(files).filter(isValidFile);
      const finalFiles = singleFile ? validFiles.slice(0, 1) : validFiles;

      if (maxFiles && finalFiles.length > Number(maxFiles)) {
        console.error("Too many files selected");
        return;
      }

      const dataTransfer = new DataTransfer();
      finalFiles.forEach((file) => dataTransfer.items.add(file));

      if (inputRef.current) {
        inputRef.current.files = dataTransfer.files;
      }
      setSelectedFiles(dataTransfer.files);

      if (onChange) {
        onChange(dataTransfer.files);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files) {
      const validFiles = Array.from(files).filter(isValidFile);
      const finalFiles = singleFile ? validFiles.slice(0, 1) : validFiles;

      if (maxFiles && finalFiles.length > Number(maxFiles)) {
        console.error("Too many files selected");
        return;
      }

      setSelectedFiles(files);

      if (onChange) {
        onChange(files);
      }
    }
  };

  const isValidFile = (file: File): boolean => {
    if (maxFileSize && file.size > Number(maxFileSize)) {
      console.error(`File ${file.name} exceeds max file size`);

      return false;
    }

    return true;
  };

  return (
    <>
      {label && <label className="text-sm text-primary-600">{label}</label>}

      <label
        className={`
          flex min-w-[400px] cursor-pointer flex-col items-center justify-center
          rounded-md border border-dashed border-primary-400 bg-cta-50 text-sm
          text-primary-400

          ${sizeStyles[size]}

          focus:outline-none focus:ring-2 focus:ring-indigo-500
          focus:ring-offset-2
        `}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          accept={accept}
          className="sr-only"
          data-testid={testId}
          multiple={!singleFile}
          name={name}
          onChange={handleChange}
          ref={inputRef}
          type="file"
        />

        {children}

        {selectedFiles ? (
          <div
            className="text-center text-sm text-primary-600"
            data-testid="files-to-upload"
          >
            {Array.from(selectedFiles).map((file) => (
              <div key={file.name}>{file.name}</div>
            ))}
          </div>
        ) : null}

        {children || selectedFiles ? null : (
          <div>Drag & Drop or click to upload</div>
        )}
      </label>

      {helperText && <p className="text-xs text-primary-600">{helperText}</p>}
      {errorText && <p className="text-xs text-alert-600">{errorText}</p>}
    </>
  );
};
